
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { EnglishApplicationForm } from "@/components/loan/EnglishApplicationForm";
import { EnglishFinancialSystem } from "@/components/loan/EnglishFinancialSystem";
import { EnglishProcessSteps } from "@/components/loan/EnglishProcessSteps";
import { EnglishFAQSection } from "@/components/loan/EnglishFAQSection";
import { EnglishContactInfo } from "@/components/loan/EnglishContactInfo";
import { useForm } from "react-hook-form";
import { FormValues, formSchema } from "@/types/loan";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoanPurpose } from "@/services/api/types";
import { useLoanCalculator } from "@/hooks/useLoanCalculator";
import { LegalDialogs } from "@/components/loan/LegalDialogs";
import { useApplicationSubmit } from "@/hooks/useApplicationSubmit";
import { ErrorAlert } from "@/components/loan/ErrorAlert";

const EnglishApplyPage = () => {
  const [loanAmount, setLoanAmount] = useState(15000);
  const [loanTerm, setLoanTerm] = useState(5);
  const [loanType, setLoanType] = useState("kulutusluotto");
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const { isSubmitting, submitError, handleSubmit } = useApplicationSubmit();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      loanPurpose: LoanPurpose.Other,
      loanAmount: 15000,
      loanTerm: 5,
      termsAccepted: false,
      marketingConsent: false,
      loanType: "kulutusluotto"
    }
  });
  
  const { monthlyPayment, totalPaid, interestAndFees } = useLoanCalculator(loanAmount, loanTerm, loanType);
  const interestRate = ((interestAndFees / (loanAmount * loanTerm)) * 100).toFixed(2);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-blue-700 mb-2">Apply for a Loan</h1>
          <p className="text-lg text-gray-600">
            Fill out the form below to receive personalized loan offers from our Finnish partner banks
          </p>
        </div>

        {submitError && <ErrorAlert error={submitError} />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2">
            <EnglishApplicationForm 
              form={form}
              handleSubmit={handleSubmit}
              loanAmount={loanAmount}
              setLoanAmount={setLoanAmount}
              loanTerm={loanTerm}
              setLoanTerm={setLoanTerm}
              interest={parseFloat(interestRate)}
              isSubmitting={isSubmitting}
              monthlyPayment={monthlyPayment}
              totalPaid={totalPaid}
              interestAndFees={interestAndFees}
              monthlyFee={5}
              setupFee={150}
              setTermsOpen={setTermsOpen}
              setPrivacyPolicyOpen={setPrivacyPolicyOpen}
              setLoanType={setLoanType}
            />
          </div>
          
          <div className="space-y-8">
            <EnglishFinancialSystem />
            <EnglishProcessSteps />
          </div>
        </div>

        <EnglishFAQSection />
        <EnglishContactInfo />

        <div className="mt-12 text-center">
          <Link 
            to="/en" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <ArrowLeft className="mr-2" />
            Back to Homepage
          </Link>
        </div>
      </div>
      
      <LegalDialogs
        termsOpen={termsOpen}
        setTermsOpen={setTermsOpen}
        privacyPolicyOpen={privacyPolicyOpen}
        setPrivacyPolicyOpen={setPrivacyPolicyOpen}
        language="en"
      />
    </div>
  );
};

export default EnglishApplyPage;
